import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import resolveInterviewDetail, {
	type InterviewDetailsSource,
} from '@UI/Pages/Beneficiaries/Hooks/useRetrieveInterviewDetail/resolveInterviewDetail';
import useBeneficiaryInterviewsList from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList';
import resolveUploadStage, {
	type UploadStage,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload/Helpers/resolveUploadStage';
import useOutboxHydration from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload/Hooks/useOutboxHydration';
import useOutboxItems from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxItems';
import captureInProgressSignal from '@UI/Guidelines/Shell/Signals/captureInProgressSignal';
import { useSignalValue } from '@verclam/pulsar';
import * as React from 'react';

type UploadSubject = {
	readonly stage: UploadStage;
	readonly kind: InterviewKind;
	/** Heading of the page; the interview is not always known yet. */
	readonly title: string;
	/** Participants, as a sentence; `undefined` when there is none to name. */
	readonly participants: string | undefined;
	/** The interview as the server knows it; `null` while only this device does. */
	readonly serverInterviewId: number | null;
	/** The server list's own failure. */
	readonly error: Error | null;
	readonly refetch: () => void;
};

/**
 * Gathers what the upload page knows of its interview from the three places it can live — this
 * device's queue, this device's local interviews, the server list — and settles on a stage.
 *
 * While the server does not know the interview yet, the list is read once more at the moment the
 * queue item of this device turns `confirmed`; the page then follows the interview itself.
 */
const useUploadSubject = (subject: SubjectAddress, source: InterviewDetailsSource): UploadSubject => {
	const {
		serverInterviews,
		localInterviews,
		participantLabels,
		isServerDataLoaded,
		isLocalStoreHydrated,
		error,
		refetch,
	} = useBeneficiaryInterviewsList(subject);
	const outboxItems = useOutboxItems();
	const outboxHydration = useOutboxHydration();
	const isCaptureInProgress = useSignalValue(captureInProgressSignal);

	const details = React.useMemo(
		() =>
			resolveInterviewDetail(source, serverInterviews, localInterviews, outboxItems, participantLabels),
		[source, serverInterviews, localInterviews, outboxItems, participantLabels],
	);
	const interviewId = details?.interviewId ?? null;
	const serverInterview =
		interviewId !== null
			? (serverInterviews.find((interview) => interview.id === interviewId) ?? null)
			: null;
	const serverInterviewId = serverInterview?.id ?? null;

	const outboxItemId = details?.outboxItem?.id ?? null;
	const outboxItemState = details?.outboxItem?.state ?? null;
	const lastSeenOutboxStateRef = React.useRef<{ id: string; state: string } | null>(null);

	React.useEffect(() => {
		if (outboxItemId === null || outboxItemState === null) {
			return;
		}

		const lastSeen = lastSeenOutboxStateRef.current;
		lastSeenOutboxStateRef.current = { id: outboxItemId, state: outboxItemState };

		if (
			lastSeen?.id === outboxItemId &&
			lastSeen.state !== 'confirmed' &&
			outboxItemState === 'confirmed' &&
			serverInterviewId === null
		) {
			refetch();
		}
	}, [outboxItemId, outboxItemState, serverInterviewId, refetch]);

	return React.useMemo(() => {
		const stage = resolveUploadStage({
			outboxItem: details?.outboxItem ?? null,
			serverInterview,
			isServerSettled: isServerDataLoaded || error !== null,
			isLocalStoreHydrated,
			outboxHydration,
			isCaptureInProgress,
		});

		const participantNames = details?.participants.map((participant) => participant.label).join(', ');

		return {
			stage,
			kind: details?.kind ?? InterviewKind.CONVERSATION,
			title: details?.title ?? 'Enregistrement',
			participants:
				participantNames !== undefined && participantNames !== '' ? participantNames : undefined,
			serverInterviewId,
			error,
			refetch,
		};
	}, [
		details,
		serverInterview,
		serverInterviewId,
		isServerDataLoaded,
		isLocalStoreHydrated,
		outboxHydration,
		isCaptureInProgress,
		error,
		refetch,
	]);
};

export default useUploadSubject;
